import React, { useEffect, useMemo, useRef } from "react";
import { getResourceAppMerchantData } from "utils/mapStateToProps";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withRouter } from "next/router";
import { Formik } from "formik";
import TemplateBlockPicker from "../TemplateBlockPicker";
import TemplateProductPicker from "../TemplateProductPicker";
import TemplatePricesPicker from "../TemplatePricesPicker";
import TemplateFeaturesPicker from "../TemplateFeaturesPicker";
import { ALL_TEMPLATE_PAGE_BUILDER_SECTIONS } from "utils/constants/template";
import { createManifestFromTemplateBuilderParams } from "utils/manifest";
import { createManifestAndProductsFromTemplateBuilder } from "actions/manifest";
import { interpolateRoute } from "utils/route";
import classnames from "classnames";
import PATHS from "utils/paths";
import { TEMPLATE_PAGE_BUILDER_SCHEMA } from "constants/validation";
import SplitContainer from "components/SplitContainer";
import { fetchMerchantAccounts } from "actions/merchantAccount";
import Analytics, {
  EVENT_TEMPLATE_PAGE_BUILDER_START,
  EVENT_TEMPLATE_PAGE_BUILDER_COMPLETE,
  TEMPLATE_PAGE_BUILDER
} from "utils/analytics";
import {
  getTemplatePageCompleteEventMetadata,
  getTemplatePageInitialValues
} from "utils/template";
import { TEMPLATE_PAGE_TYPE_TITLE } from "utils/constants/manifest";
import { v4 as uuidv4 } from "uuid";
import { useErrorDialog } from "utils/hooks";
import { useAppEntities } from "utils/selectors";

const SPLIT_SECTIONS = [
  ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.BLOCKS,
  ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.PRODUCTS,
  ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.PRICES
];

const SECTION_COMPONENTS_MAP = {
  [ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.BLOCKS]: TemplateBlockPicker,
  [ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.PRODUCTS]: TemplateProductPicker,
  [ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.PRICES]: TemplatePricesPicker,
  [ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.FEATURES]: TemplateFeaturesPicker
};

const TemplatePageBuilderForm = ({
  actions,
  resource,
  resourceID,
  resourceUUID,
  merchantAccountUUID,
  application,
  router,
  afterCreate
}) => {
  const showError = useErrorDialog();
  const appEntities = useAppEntities();
  const linkId = useMemo(() => uuidv4(), []);

  const formRef = useRef(null);
  useEffect(() => {
    formRef.current.validateForm();
    Analytics.track(EVENT_TEMPLATE_PAGE_BUILDER_START);
    if (resourceUUID && resource && !merchantAccountUUID) {
      actions.fetchMerchantAccounts({ resourceUUID, resource });
    }
  }, []);

  const tracking = {
    link_id: linkId,
    resource,
    resource_uuid: resourceUUID,
    feature_context: TEMPLATE_PAGE_BUILDER
  };

  const initialValues = getTemplatePageInitialValues({ appEntities, tracking });

  const createPageFromTemplateBuilder = (values, formikActions) => {
    if (resourceUUID && resource && merchantAccountUUID) {
      formikActions.setSubmitting(true);
      const { template: context, copyManifestUUID } = values.form;

      const manifestData = createManifestFromTemplateBuilderParams({
        copyManifestUUID,
        context,
        title: TEMPLATE_PAGE_TYPE_TITLE[context],
        entities: values.entities,
        products: values.products,
        primaries: values.primaries,
        features: values.features,
        featureGroups: values.featureGroups,
        merchantAccountUUID
      });

      const eventMetadata = getTemplatePageCompleteEventMetadata(
        values,
        tracking
      );
      // Set short timeout to allow event time to fire
      Analytics.track(EVENT_TEMPLATE_PAGE_BUILDER_COMPLETE, eventMetadata);

      setTimeout(() => {
        actions.createManifestAndProductsFromTemplateBuilder(
          {
            resourceID,
            resourceUUID,
            resource,
            data: manifestData
          },
          (err, response) => {
            formikActions.setSubmitting(false);

            if (err) {
              showError(
                new Error(`Failed create manifest from template builder`)
              );
            } else if (afterCreate) {
              afterCreate();
            } else {
              const showPagePath = interpolateRoute(
                PATHS.APPLICATIONS_PAGES_SHOW,
                {
                  application,
                  page: {
                    uuid: response.manifest.uuid
                  }
                }
              );

              router.replace(showPagePath);
            }
          }
        );
      }, 250);
    } else {
      formikActions.setSubmitting(false);
      showError(
        new Error(
          `Incomplete manifest creation params resource=${resource} resourceUUID=${resourceUUID} merchantAccount=${merchantAccountUUID}`
        )
      );
    }
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      validationSchema={TEMPLATE_PAGE_BUILDER_SCHEMA}
      onSubmit={createPageFromTemplateBuilder}
    >
      {({ values, errors }) => {
        const activeSection = values.form.activeSection;
        const ContentComponent = SECTION_COMPONENTS_MAP[activeSection];
        const isSplitSection = SPLIT_SECTIONS.indexOf(activeSection) > -1;
        let content = ContentComponent ? (
          <div
            className={classnames("mw-app center relative h-100-ns", {
              "bg-white": !isSplitSection
            })}
          >
            <ContentComponent
              tracking={tracking}
              resource={resource}
              resourceUUID={resourceUUID}
              merchantAccountUUID={merchantAccountUUID}
            />
          </div>
        ) : // Add a gracefull fallback here if no component found
        null;

        return <SplitContainer>{content}</SplitContainer>;
      }}
    </Formik>
  );
};

const mapStateToProps = (
  state,
  { router, application, resource, resourceUUID, resourceID }
) => {
  const result = getResourceAppMerchantData(state, {
    router,
    application,
    resource,
    resourceUUID,
    resourceID
  });

  return result;
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchMerchantAccounts,
      createManifestAndProductsFromTemplateBuilder
    },
    dispatch
  )
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TemplatePageBuilderForm)
);
